import React, { useEffect, useState } from 'react';

import { ChevronDown, ChevronUp, X } from 'lucide-react';

import DnaCoveragePanel from '@/components/DnaCoverage/DnaCoveragePanel';
import KnowledgeGapsPanel from '@/components/KnowledgeGaps/KnowledgeGapsPanel';
import { ultraDebugStore } from '@/components/UltraDebug/ultraDebugStore';

const isDev = process.env.NODE_ENV !== 'production';

export default function UltraDebugPanel() {
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [payload, setPayload] = useState(ultraDebugStore.getSnapshot());

  useEffect(() => ultraDebugStore.subscribe(() => setPayload(ultraDebugStore.getSnapshot())), []);

  useEffect(() => {
    const onKey = e => {
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 'u') {
        e.preventDefault();
        setOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (!isDev || !open) return null;

  const debug = payload?.debug;

  return (
    <div className="fixed bottom-4 left-4 z-[9998] w-[520px] max-w-[95vw]">
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl">
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 dark:border-slate-700">
          <div className="text-xs font-semibold text-slate-700 dark:text-slate-200">
            UltraPipeline Debug (Ctrl+Shift+U)
          </div>
          <div className="flex items-center gap-2">
            <button
              className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-200"
              onClick={() => setCollapsed(prev => !prev)}
              title={collapsed ? 'Expand' : 'Collapse'}
            >
              {collapsed ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            <button
              className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-200"
              onClick={() => setOpen(false)}
              title="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        {!collapsed && (
          <div className="max-h-[520px] overflow-auto p-3 space-y-4 text-xs">
            {!debug ? (
              <div className="text-slate-500">No Ultra debug payload captured yet.</div>
            ) : (
              <>
                <section className="space-y-2">
                  <div className="font-semibold text-slate-700 dark:text-slate-200">Token Usage</div>
                  <div>
                    Prompt: {debug.tokenUsage?.promptTokens ?? '—'} · Completion:{' '}
                    {debug.tokenUsage?.completionTokens ?? '—'} · Total:{' '}
                    {debug.tokenUsage?.totalTokens ?? '—'}
                  </div>
                </section>

                <section className="space-y-2">
                  <div className="font-semibold text-slate-700 dark:text-slate-200">Pipeline Steps</div>
                  <div className="space-y-1">
                    {(debug.steps || []).slice(-12).map((step, idx) => (
                      <div key={idx}>
                        {step.step}: {typeof step.message === 'string' ? step.message : '...'}
                      </div>
                    ))}
                  </div>
                </section>

                <section className="space-y-2">
                  <div className="font-semibold text-slate-700 dark:text-slate-200">Branch Decisions</div>
                  <pre className="whitespace-pre-wrap text-[11px]">
                    {JSON.stringify(debug.decisions || {}, null, 2)}
                  </pre>
                </section>

                <section className="space-y-2">
                  <div className="font-semibold text-slate-700 dark:text-slate-200">
                    Retrieval Scoring
                  </div>
                  <div>Avg Score: {debug.retrieval?.avgScore ?? '—'}</div>
                  <div className="space-y-1">
                    {(debug.retrieval?.scores || []).slice(0, 6).map((s, idx) => (
                      <div key={idx}>
                        {s.fileName || s.fileId || 'source'} · {s.score?.toFixed?.(3) ?? s.score}
                      </div>
                    ))}
                  </div>
                </section>

                <section className="space-y-2">
                  <div className="font-semibold text-slate-700 dark:text-slate-200">
                    Citation Verification
                  </div>
                  <div>
                    Verified: {debug.citations?.verified ?? 0} · Rejected:{' '}
                    {debug.citations?.rejected ?? 0}
                  </div>
                </section>

                <section className="space-y-2">
                  <div className="font-semibold text-slate-700 dark:text-slate-200">Memory Hits</div>
                  <div>Hits: {debug.memory?.hits ?? 0}</div>
                  {Array.isArray(debug.memory?.top) && debug.memory.top.length > 0 ? (
                    <div className="space-y-1">
                      {debug.memory.top.map((m, idx) => (
                        <div key={idx}>
                          {m.source || m.query || 'memory'} · {m.score ?? '—'}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </section>

                <section className="space-y-2">
                  <div className="font-semibold text-slate-700 dark:text-slate-200">Cache</div>
                  <div>
                    Hit: {debug.cache?.hit ? 'yes' : 'no'} · Bypassed:{' '}
                    {debug.cache?.bypassed ? 'yes' : 'no'}
                  </div>
                  {debug.cache?.cacheId ? <div>Cache ID: {debug.cache.cacheId}</div> : null}
                  {debug.cache?.hitCount ? <div>Hit Count: {debug.cache.hitCount}</div> : null}
                </section>

                <section className="space-y-2">
                  <div className="font-semibold text-slate-700 dark:text-slate-200">
                    Agent Routing
                  </div>
                  <pre className="whitespace-pre-wrap text-[11px]">
                    {JSON.stringify(debug.routing || {}, null, 2)}
                  </pre>
                </section>

                <section className="space-y-2">
                  <div className="font-semibold text-slate-700 dark:text-slate-200">
                    Citation Verifier Paths
                  </div>
                  {Array.isArray(debug.citations?.paths) && debug.citations.paths.length > 0 ? (
                    <div className="space-y-1">
                      {debug.citations.paths.slice(0, 8).map((p, idx) => (
                        <div key={idx}>
                          {p.fileName || 'source'} · {p.valid ? 'valid' : 'rejected'} ·{' '}
                          {p.similarity ?? '—'}
                          {!p.valid && p.reason ? ` (${p.reason})` : ''}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-slate-500">No citation paths recorded.</div>
                  )}
                </section>

                <section className="space-y-2">
                  <div className="font-semibold text-slate-700 dark:text-slate-200">
                    Engine Fallbacks
                  </div>
                  {Array.isArray(debug.fallbacks) && debug.fallbacks.length > 0 ? (
                    <div className="space-y-1">
                      {debug.fallbacks.map((f, idx) => (
                        <div key={idx}>
                          {f.step}: {f.message}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-slate-500">No fallbacks recorded.</div>
                  )}
                </section>

                <section className="space-y-2">
                  <DnaCoveragePanel coverage={payload?.dnaCoverage} />
                </section>

                <section className="space-y-2">
                  <KnowledgeGapsPanel gaps={payload?.knowledgeGaps} />
                </section>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
